import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { TwBtnComponent } from '@twist/ui-primitives';

/**
 * `tw-secret-reveal` — one-time reveal surface for a freshly issued secret
 * (API key token, credential, ...).
 *
 * Contract:
 * - States plainly that this is the only time the secret is shown.
 * - The secret is rendered through text interpolation only — it is never
 *   passed through `innerHTML` and never concatenated into markup, so a
 *   secret containing markup-like characters stays inert text.
 * - The component keeps its own copy of the secret in `revealedSecret`
 *   and clears it in `ngOnDestroy`, so the plaintext does not outlive the
 *   surface (modal, drawer, ...) that revealed it.
 * - Copy-to-clipboard is built in via `navigator.clipboard`; no consumer
 *   needs its own clipboard plumbing.
 *
 * Consumers hand over the secret via the non-required `secret` input and
 * an optional `subject` naming what was issued (defaults to "secret").
 * Any surface that receives a one-time token on create or regenerate can
 * embed this component directly in its success state.
 */
@Component({
  selector: 'tw-secret-reveal',
  standalone: true,
  imports: [TwBtnComponent],
  templateUrl: './tw-secret-reveal.component.html',
  styleUrl: './tw-secret-reveal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwSecretRevealComponent implements OnDestroy {
  /** The one-time plaintext secret. Non-required: surfaces usually receive it async. */
  readonly secret = input<string | undefined>(undefined);

  /** What the secret is, for the notice copy (e.g. "API key"). */
  readonly subject = input<string>('secret');

  /**
   * Internal copy that drives the template. Kept separate from the input
   * so the component can wipe it when the surface closes.
   */
  readonly revealedSecret = signal<string | undefined>(undefined);

  /** True for a short moment after a successful clipboard write. */
  readonly copied = signal(false);

  private readonly syncSecret = effect(() => {
    this.revealedSecret.set(this.secret());
  });

  private copiedResetHandle: ReturnType<typeof setTimeout> | undefined;

  async copy(): Promise<void> {
    const value = this.revealedSecret();
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      this.copied.set(true);
      if (this.copiedResetHandle !== undefined) {
        clearTimeout(this.copiedResetHandle);
      }
      this.copiedResetHandle = setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Clipboard access denied — the secret stays visible for manual copy.
    }
  }

  ngOnDestroy(): void {
    if (this.copiedResetHandle !== undefined) {
      clearTimeout(this.copiedResetHandle);
    }
    this.revealedSecret.set(undefined);
    this.copied.set(false);
  }
}
